/**
 * <big-image-gallery>
 *
 * Adds a thumbnail gallery to the "big image" product layout.
 * - Clicking a thumbnail swaps the main (hero) image client-side, with no network request.
 * - Selecting a variant calls setActiveMedia() (from section-product.js) so the hero and the
 *   active thumbnail stay in sync with the chosen variant's image.
 *
 * Each media's full-size markup (responsive <picture> for images, autoplaying <video> or video
 * iframe for videos) is pre-rendered inside an inert <template>. Media in a <template> is not
 * fetched until the node is cloned into the DOM, so swapping keeps correct srcset/sizes without
 * building URLs in JS and adds no requests on load. Cloning a new media removes (stops) the
 * previous one.
 *
 * The thumbnail rail lives in a dedicated container outside this element, matched by section id
 * via [data-big-image-thumbs="<section>"]. Templates and the stage stay inside this element.
 */
if (!customElements.get('big-image-gallery')) {
  class BigImageGallery extends HTMLElement {
    connectedCallback() {
      this.stage = this.querySelector('[data-big-image-stage]');
      this.templates = Array.from(this.querySelectorAll('[data-big-image-template]'));

      // Variant -> media id map, built server-side (see the section template). Used to resolve the
      // hero image on variant change without relying on featured_media being present in the
      // picker's variant JSON.
      this.variantMediaMap = {};
      const mapEl = this.querySelector('[data-big-image-variant-media]');
      if (mapEl) {
        try {
          this.variantMediaMap = JSON.parse(mapEl.textContent) || {};
        } catch (e) {
          this.variantMediaMap = {};
        }
      }

      const sectionId = this.dataset.section;
      this.thumbsContainer = sectionId
        ? document.querySelector(`[data-big-image-thumbs="${sectionId}"]`)
        : null;
      this.thumbs = this.thumbsContainer
        ? Array.from(this.thumbsContainer.querySelectorAll('[data-big-image-thumb]'))
        : [];

      // Swap the hero when the variant changes. The theme's VariantSelects updates the hidden
      // <input name="id"> and dispatches a bubbling 'change' on it; we map that variant id to its
      // media and show it. Kept here (not in section-product.js) so the theme JS is untouched.
      this.onVariantInputChange = (event) => {
        const target = event.target;
        if (target && target.matches && target.matches('input[name="id"]')) {
          this.setActiveVariant(target.value);
        }
      };
      document.addEventListener('change', this.onVariantInputChange);

      if (!this.thumbs.length) return;

      this.onThumbClick = this.onThumbClick.bind(this);
      this.thumbs.forEach((thumb) => thumb.addEventListener('click', this.onThumbClick));
    }

    disconnectedCallback() {
      if (this.onVariantInputChange) {
        document.removeEventListener('change', this.onVariantInputChange);
      }
      this.thumbs?.forEach((thumb) => thumb.removeEventListener('click', this.onThumbClick));
    }

    onThumbClick(event) {
      const mediaId = event.currentTarget.dataset.mediaId;
      this.setActiveMedia(mediaId, { focusThumb: false });
    }

    /**
     * Show the image associated with a variant (resolved via the variant -> media map).
     * No-op when the variant has no associated media, leaving the current hero in place.
     * @param {string|number} variantId
     */
    setActiveVariant(variantId) {
      if (variantId == null) return;
      const mediaId = this.variantMediaMap[String(variantId)];
      if (mediaId != null) this.setActiveMedia(mediaId);
    }

    /**
     * Show the given media in the hero stage and mark its thumbnail active.
     * @param {string|number} mediaId - product media id (matches data-media-id)
     * @param {Object} [options]
     * @param {boolean} [options.focusThumb=false] - keep focus on the active thumbnail
     */
    setActiveMedia(mediaId, { focusThumb = false } = {}) {
      if (mediaId == null) return;
      const id = String(mediaId);

      const template = this.templates.find((tpl) => tpl.dataset.mediaId === id);
      const thumb = this.thumbs.find((btn) => btn.dataset.mediaId === id);

      // Swap the hero media (only when we have a pre-rendered template and it isn't already shown).
      // Works for images and videos alike; replaceChildren removes/stops the previous media.
      if (this.stage && template && this.stage.dataset.mediaId !== id) {
        this.stage.replaceChildren(template.content.cloneNode(true));
        this.stage.dataset.mediaId = id;
      }

      // Sync active state across thumbnails.
      this.thumbs.forEach((btn) => {
        const isActive = btn === thumb;
        btn.classList.toggle('active', isActive);
        btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });

      if (thumb) {
        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        thumb.scrollIntoView({
          block: 'nearest',
          inline: 'nearest',
          behavior: prefersReducedMotion ? 'auto' : 'smooth',
        });
        if (focusThumb) thumb.focus();
      }
    }
  }

  customElements.define('big-image-gallery', BigImageGallery);
}
